import { useEffect, useState } from "react";
import { MousePointerClick, Users, PartyPopper, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card, SectionTitle } from "../ui";

const usd = (n) => `$${Number(n || 0).toLocaleString()}`;
const STATUS_NEXT = { pending: "relocated", relocated: "paid" };
const STATUS_STYLE = {
  pending: "bg-sand-200 text-charcoal-600",
  relocated: "bg-terracotta-light text-terracotta-700",
  paid: "bg-sage/15 text-sage",
  void: "bg-sand-100 text-charcoal-400 line-through",
};

export default function ReferralsManager() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () =>
    api.get("/admin/referrals").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (ref, status) => {
    setBusy(ref.id);
    try {
      await api.patch(`/admin/referrals/${ref.id}`, { status });
      toast.success(status === "relocated"
        ? `Marked relocated — $${ref.reward_amount} now payable to ${ref.referrer_name}.`
        : `Referral marked ${status}.`);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy("");
  };

  if (!data) return <p className="text-charcoal-500">Loading referrals…</p>;
  const { stats, referrals } = data;

  return (
    <div className="space-y-8" data-testid="admin-referrals">
      <SectionTitle title="Referrals" subtitle="$100 per referred client who relocates to Mexico. Payouts are handled manually by you." />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={MousePointerClick} label="Link visits" value={stats.visits_link} testid="ref-stat-link" />
        <Stat icon={MousePointerClick} label="Email referral visits" value={stats.visits_email} testid="ref-stat-email" />
        <Stat icon={Users} label="People referred" value={stats.total} testid="ref-stat-total" />
        <Stat icon={PartyPopper} label="Owed now" value={usd(stats.owed)} accent testid="ref-stat-owed" />
      </div>

      <Card>
        {referrals.length === 0 ? (
          <p className="text-sm text-charcoal-500 py-4" data-testid="ref-empty">
            No referrals yet. Clients find their share link in the Client Portal under "Refer & earn $100".
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-charcoal-400 border-b border-sand-200">
                  <th className="py-2 pr-4">Referred person</th>
                  <th className="py-2 pr-4">Referred by</th>
                  <th className="py-2 pr-4">Via</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.id} className="border-b border-sand-100" data-testid={`ref-row-${r.id}`}>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-charcoal-800">{r.referred_name || "—"}</div>
                      <div className="text-xs text-charcoal-500">{r.referred_email}</div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-charcoal-700">{r.referrer_name}</div>
                      <div className="text-xs text-charcoal-500">{r.code}</div>
                    </td>
                    <td className="py-3 pr-4 text-charcoal-600">{r.source}</td>
                    <td className="py-3 pr-4">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[r.status]}`}>
                        {r.status === "relocated" ? `relocated · ${usd(r.reward_amount)} owed` : r.status}
                      </span>
                    </td>
                    <td className="py-3 space-x-2 whitespace-nowrap">
                      {STATUS_NEXT[r.status] && (
                        <button
                          data-testid={`ref-advance-${r.id}`}
                          disabled={busy === r.id}
                          onClick={() => setStatus(r, STATUS_NEXT[r.status])}
                          className="rounded-full bg-terracotta px-3 py-1.5 text-xs font-medium text-white hover:bg-terracotta-hover disabled:opacity-50"
                        >
                          {r.status === "pending" ? "Mark relocated ✓" : `Mark ${usd(r.reward_amount)} paid`}
                        </button>
                      )}
                      {r.status === "pending" && (
                        <button
                          data-testid={`ref-void-${r.id}`}
                          disabled={busy === r.id}
                          onClick={() => setStatus(r, "void")}
                          className="rounded-full border border-sand-200 px-3 py-1.5 text-xs text-charcoal-500 hover:bg-sand-50"
                        >
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-charcoal-400">
        Tip: set your Google review link under <span className="font-medium">Brand → Google review link</span> — it appears in the site footer, the Client Portal, and post-move emails. <ExternalLink size={12} className="inline" />
      </p>
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent, testid }) {
  return (
    <div className={`rounded-2xl border border-sand-200 bg-white px-5 py-4 ${accent ? "border-terracotta/40" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-charcoal-400">
        <Icon size={14} /> {label}
      </div>
      <div className={`mt-1 text-2xl font-serif ${accent ? "text-terracotta" : "text-charcoal-800"}`}>{value}</div>
    </div>
  );
}
